import * as React from 'react';
import {emotionStyled} from '@packages/utilities/styled';
import {unselectable} from '@packages/systems/design-system/constants/Styles';
import {Text} from '@packages/systems/spring';

const ItemWrapper = emotionStyled('div')(
  {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
    backgroundColor: '$background1',
  },
  {
    variants: {
      isDraggable: {
        true: {
          cursor: '-webkit-grab',
        },
      },
      isDragging: {true: {}},
      upgradeExperiment: {
        true: {
          cursor: 'default',
        },
      },
      isDisabled: {
        true: {
          pointerEvents: 'all',
          cursor: 'not-allowed',
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          isDraggable: true,
          isDragging: true,
        },
        css: {
          cursor: '-webkit-grabbing',
        },
      },
    ],
  }
);

const IconWrapperView = emotionStyled('div')(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 'auto',
    padding: '$4',
    flex: '1 0 auto',
    flexGrow: 1,
    flexShrink: 0,
    backgroundColor: '$background2',
    borderRadius: '$2',
  },
  {
    variants: {
      isDisabled: {
        true: {
          opacity: 0.5,
        },
      },
      isHover: {
        true: {
          backgroundColor: '$uiBackgroundHover',
        },
      },
      isFromLibrary: {
        true: {
          alignItems: 'flex-start',
          flexGrow: 1,
        },
      },
    },
  }
);

const TextView = emotionStyled(Text)(
  {
    display: 'flex',
    flexGrow: 0,
    flexShrink: 0,
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 0 0',
    justifyContent: 'flex-start',
    color: '$textSecondary',
    textAlign: 'center',
    height: '$10',
    width: '100%',
    ...unselectable,
    // Place `cursor: 'inherit'` after `unselectable` to override `cursor: 'default'` within `unselectable`
    cursor: 'inherit',
  },
  {
    variants: {
      isDisabled: {
        true: {
          opacity: 0.5,
        },
      },
      isDragging: {true: {}},
      isHover: {true: {}},
    },
    compoundVariants: [
      {
        conditions: {isHover: true, isDragging: false},
        css: {color: '$textPrimary'},
      },
    ],
  }
);

type AddPluginItemProps = {
  children?: React.ReactNode;
  hover?: boolean;
  dragging?: boolean;
  disabled?: boolean;
  upgradeExperiment?: boolean;
  ['data-automation-id']?: string;
  onClick: () => void;
  onMouseDown?: (event: React.MouseEvent<Element>) => void;
  onMouseMove?: () => void;
  onMouseUp?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  icon: React.ReactNode;
  name: string;
  isDraggable?: boolean; // this property can be removed when we implement DnD for Logic Blocks;
  isFromLibrary?: boolean;
};

export const AddPluginItem = React.forwardRef<
  HTMLDivElement,
  AddPluginItemProps
>(
  (
    {
      children,
      hover,
      dragging,
      disabled,
      'data-automation-id': dataAutomationId,
      upgradeExperiment,
      onClick,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseEnter,
      onMouseLeave,
      icon,
      name,
      isDraggable,
      isFromLibrary,
    },
    ref
  ) => (
    <ItemWrapper
      ref={ref}
      isDisabled={disabled}
      isDraggable={isDraggable}
      isDragging={dragging}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      upgradeExperiment={upgradeExperiment}
      data-automation-id={dataAutomationId}
    >
      <IconWrapperView
        isDisabled={disabled}
        isHover={hover}
        isFromLibrary={isFromLibrary}
      >
        {icon}
      </IconWrapperView>
      {name && (
        <TextView isDragging={dragging} isDisabled={disabled} isHover={hover}>
          {name}
        </TextView>
      )}
      {children}
    </ItemWrapper>
  )
);
